import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../hooks/usePermissions';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { 
  ChevronLeft, 
  Eye, 
  Send, 
  Save, 
  Plus, 
  Type, 
  Image as ImageIcon, 
  Layout, 
  Settings, 
  Smartphone, 
  Mail, 
  Monitor,
  Trash2,
  MoveUp,
  MoveDown,
  CheckCircle2,
  Sparkles,
  Search,
  MessageSquare,
  History,
  TrendingUp,
  ExternalLink,
  Split,
  Users,
  AlertCircle,
  Star
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, Reorder, AnimatePresence } from 'motion/react';
import { AccessGate } from '../components/ui/AccessGate';
import { hasTierAccess, TIER_GATED_FEATURES } from '../constants';

// Section ids only need uniqueness within this page session. Keep Math.random() out of
// the component render scope (event-handler closures are linted as render-time).
const makeSectionId = () => `sec-${Math.random().toString(36).substr(2, 9)}`;

import { 
  Newsletter,
  NewsletterSection,
  Contribution,
  HeroSection,
  TextSection,
  StorySection,
  ImageSection,
  NoticeSection,
  EventEmbedSection
} from '../types';
import { 
  useNewsletter,
  useUpdateNewsletterSections,
  useUpdateNewsletterMetadata,
  usePublishNewsletter,
  useReadyContributions, 
  useContributions,
  useApproveContribution,
  useRejectContribution,
  useNewsletterStats,
  useUploadNewsletterImage
} from '../hooks/useNewsletters';
import { useNodeRoles } from '../hooks/useNode';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';

const SECTION_TEMPLATES: Record<string, Partial<NewsletterSection>> = {
  hero: { 
    type: 'hero', 
    title: 'New Issue Headline', 
    subtitle: 'Subtitle goes here', 
    ctaText: 'Learn More',
    ctaUrl: 'https://orgsets.com',
    imageUrl: 'https://images.unsplash.com/photo-1454165833767-027eeea160d7?w=1200&auto=format&fit=crop&q=60' 
  } as HeroSection,
  text: { 
    type: 'text', 
    htmlContent: '<p>Add your long-form text here. Use rich text or simple paragraphs.</p>' 
  } as TextSection,
  story: { 
    type: 'story', 
    title: 'Member Spotlight', 
    content: 'Member story goes here...', 
    author: 'Benjamin Franklin' 
  } as StorySection,
  image: { 
    type: 'image', 
    imageUrl: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&auto=format&fit=crop&q=60', 
    caption: 'Image caption here' 
  } as ImageSection,
  notice: { 
    type: 'notice', 
    title: 'Important Announcement', 
    content: 'A critical notice for all members to see.', 
    severity: 'info' 
  } as NoticeSection,
  button: {
    type: 'button',
    text: 'Click Here to Join',
    url: 'https://orgsets.com'
  } as any,
  divider: {
    type: 'divider'
  } as any
};

export const NewsletterStudioPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { node, brand } = useTenant();
  const { can, isAdmin, isSystemAdmin, isSuperAdmin, role } = usePermissions();
  const navigate = useNavigate();
  
  const [viewMode, setViewMode] = useState<'edit' | 'preview' | 'stats'>('edit');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [activeTab, setActiveTab] = useState<'elements' | 'library' | 'review' | 'audience' | 'settings'>('elements');
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  // Data Fetching
  const { data: newsletter, isLoading: newsletterLoading } = useNewsletter(id || '');
  const { data: stats } = useNewsletterStats(id || '');
  const { data: readyContributions = [] } = useReadyContributions(node?.id || '');
  const { data: allContributions = [] } = useContributions(node?.id || '');
  const { data: roles = [] } = useNodeRoles(node?.id || '');

  // Local State (for instant editing before sync)
  const [localTitle, setLocalTitle] = useState('');
  const [localSubjectLine, setLocalSubjectLine] = useState('');
  const [localPreviewText, setLocalPreviewText] = useState('');
  const [localSections, setLocalSections] = useState<NewsletterSection[]>([]);

  // Mutations
  const updateSections = useUpdateNewsletterSections(id || '');
  const updateMetadata = useUpdateNewsletterMetadata(id || '');
  const publishNewsletter = usePublishNewsletter(id || '');
  const approveContribution = useApproveContribution(node?.id || '');
  const rejectContribution = useRejectContribution(node?.id || '');
  const uploadImage = useUploadNewsletterImage(id || '');

  const [prevNewsletter, setPrevNewsletter] = useState<typeof newsletter | null>(null);
  if (newsletter && prevNewsletter !== newsletter) {
    setPrevNewsletter(newsletter);
    setLocalTitle(newsletter.title || '');
    setLocalSubjectLine(newsletter.subjectLine || '');
    setLocalPreviewText(newsletter.previewText || '');
    setLocalSections(newsletter.sections || []);
  }

  const handleSave = async () => {
    if (!newsletter) return;
    await updateSections.mutateAsync({ 
      sections: localSections, 
      version: newsletter.version 
    });
    await updateMetadata.mutateAsync({
      title: localTitle,
      subjectLine: localSubjectLine,
      previewText: localPreviewText
    });
  };

  const handlePublish = async () => {
    if (!id || !newsletter) return;
    try {
      await updateSections.mutateAsync({ 
        sections: localSections, 
        version: newsletter.version 
      });
      await updateMetadata.mutateAsync({
        title: localTitle,
        subjectLine: localSubjectLine,
        previewText: localPreviewText
      });
      await publishNewsletter.mutateAsync();
      toast.success('Newsletter enroute to distribution engine');
      navigate('/newsletter');
    } catch (err) {
      toast.error('Failed to publish newsletter');
    }
  };

  if (!hasTierAccess('newsletterStudio', node?.tier, { isSystemAdmin, isSuperAdmin })) {
    return (
      <AccessGate
        reason="tier"
        currentTier={node?.tier || 'LOCAL'}
        requiredTiers={TIER_GATED_FEATURES.newsletterStudio}
        canResolve={isAdmin}
        onResolve={isAdmin ? () => navigate('/settings/billing') : undefined}
        onBack={() => navigate('/newsletter')}
      />
    );
  }

  if (!can('create_newsletter') && !can('edit_newsletter')) {
    return (
      <AccessGate
        reason="permission"
        role={role}
        requirement="You need newsletter editing permissions to open the Studio."
        onBack={() => navigate('/newsletter')}
      />
    );
  }

  if (newsletterLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50 dark:bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  const addSection = (type: string) => {
    const template = SECTION_TEMPLATES[type];
    const newSection = {
      ...template,
      id: makeSectionId(),
      orderIndex: localSections.length,
    } as NewsletterSection;
    setLocalSections([...localSections, newSection]);
    setActiveSectionId(newSection.id);
  };

  const updateSection = (id: string, updates: Partial<NewsletterSection>) => {
    setLocalSections(localSections.map(s => s.id === id ? { ...s, ...updates } as any : s));
  };

  const removeSection = (id: string) => {
    setLocalSections(localSections.filter(s => s.id !== id));
    if (activeSectionId === id) setActiveSectionId(null);
  };

  const pendingContributions = allContributions.filter((c: Contribution) => c.status === 'PENDING');

  const importSubmission = (sub: Contribution) => {
    const newSection: StorySection = {
      id: makeSectionId(),
      type: 'story',
      orderIndex: localSections.length,
      title: sub.title || (sub.authorName ? `${sub.authorName}'s Contribution` : 'Member Story'),
      author: sub.authorName || 'Anonymous',
      content: sub.content,
      imageUrl: sub.imageUrl
    };
    setLocalSections([...localSections, newSection]);
    setActiveSectionId(newSection.id);
    setActiveTab('elements');
    toast.success(`Imported contribution from ${sub.authorName || 'Member'}`);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      let url = '';
      try {
        const res = await uploadImage.mutateAsync(file);
        url = res?.url || URL.createObjectURL(file);
      } catch {
        url = URL.createObjectURL(file);
      }
      const newSection: ImageSection = {
        id: makeSectionId(),
        type: 'image',
        orderIndex: localSections.length,
        imageUrl: url,
        caption: file.name
      };
      setLocalSections([...localSections, newSection]);
      setActiveSectionId(newSection.id);
      toast.success('Resource uploaded to studio assembly');
    }
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] -m-4 md:-m-8 bg-white flex flex-col dark:bg-slate-950">
      {/* Studio Header */}
      <div className="h-16 bg-white border-b border-black dark:border-white px-6 flex items-center justify-between shrink-0 dark:bg-slate-900">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/newsletter')} className="hover:bg-white dark:bg-slate-800">
            <ChevronLeft size={20} className="mr-1" />
            Library
          </Button>
          <div className="h-8 w-px bg-gray-200 mx-2" />
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className={cn(
                "text-[10px] font-bold uppercase tracking-widest leading-none",
                newsletter?.status === 'DRAFT' ? "text-amber-600" : "text-green-600"
              )}>
                {newsletter?.status}
              </span>
              {updateSections.isPending && <Spinner size="sm" />}
            </div>
            <input 
              value={localTitle} 
              onChange={(e) => setLocalTitle(e.target.value)}
              className="text-sm font-bold text-gray-900 dark:text-gray-100 focus:outline-none focus:text-blue-700 bg-transparent"
              placeholder="Newsletter Title..."
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {viewMode === 'preview' && (
            <div className="flex bg-white p-1 rounded-none border border-black dark:border-white mr-2 dark:bg-slate-800">
              <button 
                onClick={() => setPreviewDevice('desktop')}
                className={cn(
                  "px-3 py-1.5 text-xs font-bold rounded-none transition-all flex items-center gap-1.5", 
                  previewDevice === 'desktop' ? "bg-navy-900 text-white dark:bg-white dark:text-black shadow-sm" : "text-gray-500 hover:text-gray-700"
                )}
                title="Desktop Browser View"
              >
                <Monitor size={14} />
                <span>Desktop</span>
              </button>
              <button 
                onClick={() => setPreviewDevice('mobile')}
                className={cn(
                  "px-3 py-1.5 text-xs font-bold rounded-none transition-all flex items-center gap-1.5", 
                  previewDevice === 'mobile' ? "bg-navy-900 text-white dark:bg-white dark:text-black shadow-sm" : "text-gray-500 hover:text-gray-700"
                )}
                title="Mobile Device View"
              >
                <Smartphone size={14} />
                <span>Mobile</span>
              </button>
            </div>
          )}

          <div className="flex bg-white p-1 rounded-none mr-4 dark:bg-slate-800">
            <button 
              onClick={() => setViewMode('edit')}
              className={cn("px-4 py-1.5 text-xs font-bold rounded-none transition-all flex items-center gap-2", viewMode === 'edit' ? "bg-white text-navy-900 dark:text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900" : "text-gray-500 hover:text-gray-700")}
            >
              <Layout size={14} />
              Studio
            </button>
            <button 
              onClick={() => setViewMode('preview')}
              className={cn("px-4 py-1.5 text-xs font-bold rounded-none transition-all flex items-center gap-2", viewMode === 'preview' ? "bg-white text-navy-900 dark:text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900" : "text-gray-500 hover:text-gray-700")}
            >
              <Eye size={14} />
              Simulate
            </button>
            {newsletter?.status === 'PUBLISHED' && (
              <button 
                onClick={() => setViewMode('stats')}
                className={cn("px-4 py-1.5 text-xs font-bold rounded-none transition-all flex items-center gap-2", viewMode === 'stats' ? "bg-white text-navy-900 dark:text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900" : "text-gray-500 hover:text-gray-700")}
              >
                <TrendingUp size={14} />
                Metrics
              </button>
            )}
          </div>
          
          <Button 
            variant="secondary" 
            size="sm" 
            className="h-9 px-4" 
            onClick={handleSave}
            disabled={!can('COMMUNICATION_MANAGE') && !can('create_newsletter')}
            isLoading={updateSections.isPending}
          >
            <Save size={16} className="mr-2" />
            Sync
          </Button>
          <Button 
            variant="primary" 
            size="sm" 
            className="h-9 px-6 bg-navy-900 hover:bg-navy-800 text-white rounded-none disabled:opacity-50"
            onClick={handlePublish}
            disabled={(!can('COMMUNICATION_MANAGE') && !can('create_newsletter')) || publishNewsletter.isPending || newsletter?.status === 'PUBLISHING' || newsletter?.status === 'PUBLISHED'}
          >
            <Send size={16} className="mr-2" />
            {publishNewsletter.isPending ? 'Queuing...' : newsletter?.status === 'PUBLISHING' ? 'Broadcasting...' : 'Broadcast'}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Toolbox & Library - Left Side */}
        <div className={cn("w-80 bg-white border-r border-black dark:border-white flex flex-col shrink-0 transition-all shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900", (viewMode === 'preview' || viewMode === 'stats') && "-ml-80")}>
          <div className="flex overflow-x-auto border-b border-black dark:border-white p-3 bg-white/50 gap-2 dark:bg-slate-950 scrollbar-hide">
            {[
              { id: 'elements', icon: Type, label: 'Add Section' },
              { id: 'library', icon: History, count: readyContributions.length, label: 'Images & Files' },
              { id: 'review', icon: CheckCircle2, count: pendingContributions.length, label: 'Pending Stories' },
              { id: 'audience', icon: Users, label: 'Who Gets This?' },
              { id: 'settings', icon: Settings, label: 'Email Setup' }
            ].map((tab) => (
              <button 
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "px-3 py-2.5 rounded-none transition-all flex items-center justify-center relative shrink-0", 
                  activeTab === tab.id 
                    ? "bg-white text-blue-700 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-2 border-black dark:border-white dark:bg-slate-900 font-bold" 
                    : "text-gray-600 hover:text-gray-900 hover:bg-white dark:bg-slate-800 font-medium"
                )}
              >
                <tab.icon size={18} className="mr-2" />
                <span className="text-sm whitespace-nowrap">{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white text-[10px] flex items-center justify-center rounded-none font-bold">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 scrollbar-hide">
            <AnimatePresence mode="wait">
              {activeTab === 'elements' && (
                <motion.div 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-3"
                >
                  <p className="text-sm font-bold text-gray-600 uppercase tracking-widest px-1">Available Sections</p>
                  {[
                    { id: 'hero', icon: Monitor, label: 'Main Header', desc: 'A large image with a title' },
                    { id: 'text', icon: Type, label: 'Standard Text', desc: 'Write paragraphs of text' },
                    { id: 'story', icon: Sparkles, label: 'Member Story', desc: 'Highlight a person' },
                    { id: 'image', icon: ImageIcon, label: 'Image with Caption', desc: 'Add a photo or graphic' },
                    { id: 'notice', icon: MessageSquare, label: 'Important Notice', desc: 'A colored alert box' },
                    { id: 'button', icon: ExternalLink, label: 'Action Button', desc: 'Call to action link' },
                    { id: 'divider', icon: Split, label: 'Divider Line', desc: 'Separating border line' }
                  ].map((tool) => (
                    <button 
                      key={tool.id}
                      onClick={() => addSection(tool.id)}
                      className="w-full flex items-center gap-4 p-4 rounded-none border-2 border-black dark:border-white hover:border-blue-500 hover:bg-blue-50 transition-all text-left group bg-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
                    >
                      <div className="p-3 bg-white rounded-none text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors dark:bg-slate-800">
                        <tool.icon size={24} />
                      </div>
                      <div>
                        <p className="text-base font-bold text-gray-900 dark:text-white mb-1">{tool.label}</p>
                        <p className="text-sm text-gray-600">{tool.desc}</p>
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}

              {activeTab === 'library' && (
                <motion.div 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-4"
                >
                  <label className="p-4 border-2 border-dashed border-black dark:border-white rounded-none flex flex-col items-center justify-center text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all group">
                    <div className="w-12 h-12 rounded-none bg-white flex items-center justify-center text-gray-500 mb-3 group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors dark:bg-slate-800">
                      <ImageIcon size={24} />
                    </div>
                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Upload New Image</p>
                    <p className="text-xs text-gray-600">JPG, PNG up to 10MB</p>
                    <input type="file" className="hidden" onChange={handleFileUpload} accept="image/*" />
                  </label>

                  <div className="p-4 bg-blue-50 rounded-none border-2 border-black dark:border-white border-blue-200 mb-4">
                    <p className="text-sm font-bold text-blue-800 mb-1">Approved Stories</p>
                    <p className="text-sm text-blue-700 leading-relaxed">Stories from your members that are ready to be added to the newsletter.</p>
                  </div>
                  
                  {readyContributions.map((sub: any) => (
                    <div key={sub.id} className="group relative rounded-none border-2 border-black dark:border-white overflow-hidden hover:border-blue-400 transition-all shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
                      <div className="aspect-video relative bg-white dark:bg-slate-800">
                        {sub.imageUrl ? (
                          <img src={sub.imageUrl} alt={sub.title || 'Newsletter hero image'} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <Sparkles size={32} />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button className="h-10 px-4 text-sm font-bold" variant="primary" onClick={() => importSubmission(sub)}>Add to Newsletter</Button>
                        </div>
                      </div>
                      <div className="p-3 bg-white flex flex-col border-t border-black dark:border-white dark:bg-slate-900">
                        <span className="text-sm font-bold text-gray-900 dark:text-gray-100 line-clamp-1">{sub.authorName}</span>
                        <span className="text-xs text-gray-500 mt-1">Received {new Date(sub.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}

              {activeTab === 'review' && (
                <motion.div 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-4"
                >
                  {pendingContributions.map((sub: Contribution) => (
                    <div key={sub.id} className="rounded-none border-2 border-black dark:border-white overflow-hidden bg-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
                      <div className="p-5">
                        <div className="flex justify-between items-start mb-3">
                          <p className="text-base font-bold text-gray-900 dark:text-white">{sub.authorName}</p>
                          <Badge variant="amber" className="text-xs px-2 py-1">Pending</Badge>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3 italic mb-5">"{sub.content}"</p>
                        <div className="flex gap-3">
                          <Button 
                            variant="secondary" 
                            className="flex-1 h-10 text-sm font-bold border-green-200 text-green-800 hover:bg-green-50"
                            onClick={() => approveContribution.mutate(sub.id)}
                            isLoading={approveContribution.isPending}
                          >
                            Approve Story
                          </Button>
                          <Button 
                            variant="secondary" 
                            className="flex-1 h-10 text-sm font-bold border-red-200 text-red-800 hover:bg-red-50"
                            onClick={() => rejectContribution.mutate(sub.id)}
                            isLoading={rejectContribution.isPending}
                          >
                            Reject Story
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {pendingContributions.length === 0 && (
                    <div className="text-center p-8 text-gray-500 text-sm">
                      No pending stories to review right now.
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'audience' && (
                <motion.div 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-base font-bold text-navy-900 dark:text-white flex items-center gap-2 mb-2">
                      <Users size={20} className="text-blue-600" />
                      Who Gets This Email?
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      Choose which groups of people should receive this newsletter. If no groups are selected, it goes to everyone.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Ancestry / Cascading */}
                    <div className="bg-blue-50/50 p-4 rounded-none border-2 border-black dark:border-white border-blue-200 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] cursor-pointer hover:bg-blue-50" onClick={(e) => {
                      // Prevent double toggle if they click the actual checkbox
                      if ((e.target as HTMLElement).tagName !== 'INPUT') {
                        updateMetadata.mutate({ cascadesDown: !(newsletter?.cascadesDown ?? true) });
                      }
                    }}>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="cascadesDown"
                          className="h-6 w-6 text-blue-600 border-black dark:border-white rounded focus:ring-blue-500 cursor-pointer"
                          checked={newsletter?.cascadesDown ?? true}
                          onChange={(e) => updateMetadata.mutate({ cascadesDown: e.target.checked })}
                        />
                        <label htmlFor="cascadesDown" className="text-sm font-bold text-navy-900 dark:text-white cursor-pointer select-none">
                          Send to all sub-organizations
                        </label>
                      </div>
                      <p className="mt-2 ml-9 text-sm text-blue-800 leading-relaxed select-none">
                        If checked, this email will also go to members of any groups below yours.
                      </p>
                    </div>

                    {/* Roles */}
                    <div>
                      <label className="text-sm font-bold text-gray-700 mb-3 block">Member Roles</label>
                      <div className="flex flex-wrap gap-3">
                        {roles.map(role => {
                          const roleKey = role.name || role.id;
                          const isSelected = newsletter?.audienceFilters?.roles?.includes(roleKey);
                          return (
                            <button
                              key={roleKey}
                              onClick={() => {
                                const currentRoles = newsletter?.audienceFilters?.roles || [];
                                const newRoles = isSelected 
                                  ? currentRoles.filter((key: any) => key !== roleKey)
                                  : [...currentRoles, roleKey];
                                updateMetadata.mutate({
                                  audienceFilters: {
                                    ...newsletter?.audienceFilters,
                                    roles: newRoles,
                                    includeDescendants: newsletter?.audienceFilters?.includeDescendants ?? true
                                  }
                                });
                              }}
                              className={cn(
                                "px-4 py-2 rounded-none text-sm font-bold border-2 border-black dark:border-white transition-all",
                                isSelected 
                                  ? "bg-blue-600 border-blue-600 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]" 
                                  : "bg-white border-black dark:border-white text-gray-700 hover:border-blue-400 hover:bg-blue-50 dark:bg-slate-900"
                                )}
                              >
                                {role.name} {isSelected && '✓'}
                              </button>
                            );
                          })}
                          {roles.length === 0 && <span className="text-sm text-gray-500 italic">No specific roles available.</span>}
                        </div>
                      </div>

                      {/* Membership Tiers */}
                      <div>
                        <label className="text-sm font-bold text-gray-700 mb-3 block">Membership Level</label>
                        <div className="flex flex-wrap gap-3">
                          {['LOCAL', 'FOUNDATION', 'MOMENTUM', 'INSTITUTIONAL'].map(tier => {
                            const isSelected = newsletter?.audienceFilters?.membershipTiers?.includes(tier);
                            return (
                              <button
                                key={tier}
                                onClick={() => {
                                  const currentTiers = newsletter?.audienceFilters?.membershipTiers || [];
                                  const newTiers = isSelected 
                                    ? currentTiers.filter((t: any) => t !== tier)
                                    : [...currentTiers, tier];
                                  updateMetadata.mutate({
                                    audienceFilters: {
                                      ...newsletter?.audienceFilters,
                                      membershipTiers: newTiers,
                                      includeDescendants: newsletter?.audienceFilters?.includeDescendants ?? true
                                    }
                                  });
                                }}
                                className={cn(
                                  "px-4 py-2 rounded-none text-sm font-bold border-2 border-black dark:border-white transition-all",
                                  isSelected 
                                    ? "bg-blue-600 border-blue-600 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]" 
                                    : "bg-white border-black dark:border-white text-gray-700 hover:border-blue-400 hover:bg-blue-50 dark:bg-slate-900"
                                )}
                              >
                                {tier} {isSelected && '✓'}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Interest Tags */}
                      <div>
                        <label className="text-sm font-bold text-gray-700 mb-3 block">Interest Tags</label>
                        <div className="flex flex-wrap gap-3">
                          {['Volunteer', 'Investor', 'Partner', 'Active', 'New Member'].map(tag => {
                            const isSelected = newsletter?.audienceFilters?.tags?.includes(tag);
                            return (
                              <button
                                key={tag}
                                onClick={() => {
                                  const currentTags = newsletter?.audienceFilters?.tags || [];
                                  const newTags = isSelected 
                                    ? currentTags.filter((t: any) => t !== tag)
                                    : [...currentTags, tag];
                                  updateMetadata.mutate({
                                    audienceFilters: {
                                      ...newsletter?.audienceFilters,
                                      tags: newTags,
                                      includeDescendants: newsletter?.audienceFilters?.includeDescendants ?? true
                                    }
                                  });
                                }}
                                className={cn(
                                  "px-4 py-2 rounded-none text-sm font-bold border-2 border-black dark:border-white transition-all",
                                  isSelected 
                                    ? "bg-blue-600 border-blue-600 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]" 
                                    : "bg-white border-black dark:border-white text-gray-700 hover:border-blue-400 hover:bg-blue-50 dark:bg-slate-900"
                                )}
                              >
                                {tag} {isSelected && '✓'}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-black dark:border-white flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-500 uppercase tracking-tight">Estimated Reach</span>
                        <span className="text-3xl font-black text-navy-900 dark:text-white tracking-tighter">{(node?.memberCount || 0).toLocaleString()} people</span>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === 'settings' && (
                <motion.div 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-6"
                >
                  <div>
                    <label className="text-sm font-bold text-gray-700 block mb-2">Email Subject Line</label>
                    <Input 
                      value={localSubjectLine} 
                      onChange={(e) => setLocalSubjectLine(e.target.value)}
                      onBlur={() => {
                        if (localSubjectLine !== (newsletter?.subjectLine || '')) {
                          updateMetadata.mutate({ subjectLine: localSubjectLine });
                        }
                      }}
                      className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-900 dark:border-white"
                      placeholder="e.g., June Chapter Updates"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-700 block mb-2">Preview Text (Snippet)</label>
                    <textarea 
                      className="w-full h-24 px-4 py-3 text-base border-2 border-black dark:border-white rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white scrollbar-hide dark:bg-slate-900 dark:border-white"
                      value={localPreviewText}
                      onChange={(e) => setLocalPreviewText(e.target.value)}
                      onBlur={() => {
                        if (localPreviewText !== (newsletter?.previewText || '')) {
                          updateMetadata.mutate({ previewText: localPreviewText });
                        }
                      }}
                      placeholder="A short sentence summarizing this email..."
                    />
                    <p className="text-xs text-gray-500 mt-2">This text appears next to the subject line in most email apps.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Workspace - Canvas Area */}
        <div className="flex-1 overflow-y-auto p-12 bg-white/50 scrollbar-hide relative dark:bg-slate-800">
          {viewMode === 'stats' ? (
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { label: 'Recipients', value: stats?.recipientsTotal || 0, icon: Mail },
                  { label: 'Unique Opens', value: stats?.uniqueOpens || 0, icon: Eye, color: 'text-blue-600' },
                  { label: 'Link Clicks', value: stats?.linkClicks || 0, icon: ExternalLink, color: 'text-green-600' }
                ].map((stat, i) => (
                  <Card key={i} className="p-6 border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-2 bg-white rounded-none text-gray-400 dark:bg-slate-950">
                        <stat.icon size={20} />
                      </div>
                    </div>
                    <p className="text-2xl font-black text-navy-900 dark:text-white">{(stat.value || 0).toLocaleString()}</p>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{stat.label}</p>
                  </Card>
                ))}
              </div>

              <Card className="p-8 border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
                <h3 className="text-lg font-bold text-navy-900 dark:text-white mb-8 flex items-center gap-2">
                  <Split size={20} className="text-blue-600" />
                  Delivery Cascade Breakdown
                </h3>
                <div className="space-y-6">
                  {[
                    { channel: 'Email', value: stats?.deliveredEmail || 0, target: stats?.recipientsTotal || 1, color: 'bg-blue-600' },
                    { channel: 'SMS/Mobile', value: stats?.deliveredSms || 0, target: stats?.recipientsTotal || 1, color: 'bg-green-600' },
                    { channel: 'Internal Pulse', value: stats?.deliveredPush || 0, target: stats?.recipientsTotal || 1, color: 'bg-amber-600' }
                  ].map((delivery, i) => (
                    <div key={i} className="space-y-2">
                      <div className="flex justify-between text-xs font-bold uppercase tracking-widest">
                        <span className="text-gray-900 dark:text-white">{delivery.channel}</span>
                        <span className="text-gray-400">{Math.round((delivery.value / delivery.target) * 100)}%</span>
                      </div>
                      <div className="h-2 bg-white rounded-none overflow-hidden dark:bg-slate-800">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${(delivery.value / delivery.target) * 100}%` }}
                          className={cn("h-full rounded-none transition-all duration-1000", delivery.color)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          ) : (
            <div className={cn(
              "mx-auto transition-all duration-300",
              previewDevice === 'mobile' ? "max-w-[400px]" : "max-w-[800px]"
            )}>
              <div className={cn(
                "bg-white shadow-2xl overflow-hidden min-h-[1000px] transition-all dark:bg-slate-900",
                previewDevice === 'mobile' ? "rounded-[40px] border-[12px] border-gray-950" : "rounded-none"
              )}>
                {/* Internal Newsletter Content */}
                <div className="w-full bg-[#fcfcfc]">
                  {/* Studio Header Rendering */}
                  <div className="p-8 border-b border-black dark:border-white flex items-center justify-between">
                    {node?.config?.logoUrl ? (
                      <img src={node.config?.logoUrl} alt="Logo" className="h-8 bg-slate-900/5 dark:bg-white/90 p-0.5 rounded-sm" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="text-xl font-black text-navy-900 dark:text-white uppercase tracking-tighter">{node?.name}</div>
                    )}
                    <div className="text-right">
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Official Dispatch</div>
                      <div className="text-xs font-bold text-navy-900 dark:text-white">
                        {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </div>
                    </div>
                  </div>

                  <div className="pb-20">
                    <Reorder.Group axis="y" values={localSections} onReorder={setLocalSections} className="space-y-0">
                      {localSections.map((section) => (
                        <Reorder.Item 
                          key={section.id} 
                          value={section}
                          drag={viewMode === 'edit'}
                          className={cn(
                            "relative group cursor-default transition-all duration-300",
                            activeSectionId === section.id && viewMode === 'edit' ? "ring-2 ring-blue-500 ring-inset bg-blue-50/5" : "hover:bg-white/30 dark:bg-slate-950"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (viewMode === 'edit') setActiveSectionId(section.id);
                          }}
                        >
                          {/* Editor Controls Overlay */}
                          {viewMode === 'edit' && activeSectionId === section.id && (
                            <div className="absolute top-4 right-4 z-10 flex gap-2 transition-opacity bg-white p-2 rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-2 border-black dark:border-white">
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  const idx = localSections.findIndex(s => s.id === section.id);
                                  if (idx > 0) {
                                    const newSections = [...localSections];
                                    [newSections[idx - 1], newSections[idx]] = [newSections[idx], newSections[idx - 1]];
                                    setLocalSections(newSections);
                                  }
                                }} 
                                className="px-3 py-1.5 bg-white border-2 border-black dark:border-white text-gray-700 rounded-none hover:bg-white flex items-center gap-1 font-bold text-xs"
                              >
                                <MoveUp size={14} /> Move Up
                              </button>
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  const idx = localSections.findIndex(s => s.id === section.id);
                                  if (idx < localSections.length - 1) {
                                    const newSections = [...localSections];
                                    [newSections[idx + 1], newSections[idx]] = [newSections[idx], newSections[idx + 1]];
                                    setLocalSections(newSections);
                                  }
                                }} 
                                className="px-3 py-1.5 bg-white border-2 border-black dark:border-white text-gray-700 rounded-none hover:bg-white flex items-center gap-1 font-bold text-xs"
                              >
                                <MoveDown size={14} /> Move Down
                              </button>
                              <div className="w-px bg-gray-200 mx-1"></div>
                              <button onClick={(e) => { e.stopPropagation(); removeSection(section.id); }} className="px-3 py-1.5 bg-red-50 border-2 border-black dark:border-white border-red-100 text-red-600 rounded-none hover:bg-red-100 flex items-center gap-1 font-bold text-xs">
                                <Trash2 size={14} /> Delete
                              </button>
                            </div>
                          )}

                          {/* Render Components mapping to types.ts */}
                          {section.type === 'hero' && (
                            <div className="relative h-[440px] flex items-center justify-center p-12 text-center text-white overflow-hidden">
                              {section.imageUrl && <img src={section.imageUrl} alt={section.title || 'Section background image'} className="absolute inset-0 w-full h-full object-cover -z-10" referrerPolicy="no-referrer" />}
                              <div className="absolute inset-0 bg-navy-950/60 -z-10" />
                              <div className="max-w-2xl px-6">
                                <h1 className="text-4xl md:text-5xl font-black mb-6 leading-[1.1] tracking-tight">{section.title}</h1>
                                {section.subtitle && <p className="text-lg md:text-xl text-white/90 font-medium mb-8 uppercase tracking-widest">{section.subtitle}</p>}
                                {section.ctaText && (
                                  <Button variant="primary" className="bg-white text-navy-950 hover:bg-white border-none px-8 h-12 rounded-none font-bold dark:bg-slate-900">
                                    {section.ctaText}
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}

                          {section.type === 'text' && (
                            <div className="p-12 md:p-20 max-w-3xl mx-auto">
                              <div 
                                className="prose prose-lg text-gray-700 leading-[1.8] font-serif"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.htmlContent) }}
                              />
                            </div>
                          )}

                          {section.type === 'notice' && (
                            <div className="px-12 py-10">
                              <div className={cn(
                                "border-l-[6px] p-8 rounded-r-3xl shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]",
                                section.severity === 'warning' ? "bg-amber-50 border-amber-500" : 
                                section.severity === 'error' ? "bg-red-50 border-red-500" : 
                                "bg-blue-50 border-blue-600"
                              )}>
                                <h3 className="text-navy-950 font-bold text-lg mb-3 uppercase tracking-tight flex items-center gap-2">
                                  {section.severity === 'warning' ? <Settings size={20} /> : <CheckCircle2 size={20} />}
                                  {section.title}
                                </h3>
                                <p className="text-gray-800 text-base leading-relaxed font-serif italic dark:text-white">{section.content}</p>
                              </div>
                            </div>
                          )}

                          {section.type === 'image' && (
                            <div className="px-12 py-12">
                              <div className="relative group/img overflow-hidden rounded-none shadow-2xl">
                                <img src={section.imageUrl} alt="Section featured image" className="w-full transition-transform duration-700 group-hover/img:scale-105" referrerPolicy="no-referrer" />
                              </div>
                              {section.caption && <p className="text-center mt-6 text-[11px] text-gray-400 font-bold uppercase tracking-[0.2em]">{section.caption}</p>}
                            </div>
                          )}

                          {section.type === 'story' && (
                            <div className="px-12 py-14">
                              <div className="bg-white border-2 border-gray-50 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.08)] rounded-[40px] p-12 flex flex-col items-center text-center dark:bg-slate-900">
                                <div className="w-20 h-1 bg-blue-600 mb-10 rounded-none" />
                                <h3 className="text-2xl font-black text-navy-950 mb-4 tracking-tighter leading-none">{section.title}</h3>
                                <p className="text-xl text-gray-700 italic font-serif leading-[1.7] mb-10 px-4">"{section.content}"</p>
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-none bg-navy-100 flex items-center justify-center text-navy-700 font-black text-sm border-2 border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
                                    {section.author?.charAt(0) || 'M'}
                                  </div>
                                  <div className="text-left">
                                    <p className="text-xs font-black text-navy-950 uppercase tracking-widest">{section.author}</p>
                                    <p className="text-[10px] text-gray-400 font-bold">Featured Organization Member</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {section.type === 'button' && (
                            <div className="py-8 text-center px-6">
                              <Button 
                                variant="primary"
                                className="px-8 py-3 rounded-none font-bold uppercase tracking-wider shadow-[4px_4px_0px_#000000]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if ((section as any).url) window.open((section as any).url, '_blank');
                                }}
                              >
                                {(section as any).text || 'Action Button'}
                              </Button>
                            </div>
                          )}

                          {section.type === 'divider' && (
                            <div className="py-8 max-w-3xl mx-auto px-6">
                              <hr className="border-t-2 border-black dark:border-white" />
                            </div>
                          )}

                          {section.type === 'event_embed' && (
                            <div className="px-12 py-8 max-w-3xl mx-auto">
                              <div className="bg-white border-2 border-black dark:border-white p-6 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] rounded-none dark:bg-slate-900">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="blue" className="text-[10px] uppercase font-mono tracking-widest">
                                    Featured Event
                                  </Badge>
                                </div>
                                <h3 className="text-xl font-black text-navy-900 dark:text-white mb-2">
                                  {(section as any).title || `Event #${(section as any).eventId || 'Scheduled Event'}`}
                                </h3>
                                {(section as any).content && (
                                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                                    {(section as any).content}
                                  </p>
                                )}
                                <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); navigate('/calendar'); }}>
                                  View Event Details
                                </Button>
                              </div>
                            </div>
                          )}
                        </Reorder.Item>
                      ))}
                    </Reorder.Group>
                    
                    {localSections.length === 0 && (
                      <div className="p-32 text-center border-2 border-dashed border-black dark:border-white m-12 rounded-none">
                        <Plus size={48} className="mx-auto text-gray-100 mb-4" />
                        <p className="text-sm font-bold text-gray-300 uppercase tracking-widest">Your canvas is empty.</p>
                      </div>
                    )}
                  </div>

                  {/* Email Footer */}
                  <div className="p-16 bg-navy-950 text-white text-center">
                    <div className="text-[10px] font-bold text-navy-500 uppercase tracking-[0.3em] mb-8">Follow the Chapter</div>
                    <div className="flex justify-center gap-6 mb-12">
                      {[Mail, Smartphone, Search].map((Icon, i) => (
                        <div key={i} className="w-12 h-12 rounded-none bg-navy-900 border-2 border-black dark:border-white border-navy-800 flex items-center justify-center text-navy-400 hover:text-white hover:border-white transition-all cursor-pointer">
                          <Icon size={20} />
                        </div>
                      ))}
                    </div>
                    <div className="max-w-xs mx-auto space-y-4">
                      <p className="text-[10px] text-navy-600 font-bold uppercase tracking-tight leading-relaxed">
                        Sent with precision from {node?.name} via the {brand?.name} Distribution Engine.
                      </p>
                      <div className="h-px bg-navy-900 w-12 mx-auto" />
                      <p className="text-[9px] text-navy-700 font-medium">
                        © 2024 Chapter Ops. All rights Reserved. 
                        Unsubscribe preferences available in the portal.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Property Editor */}
        <AnimatePresence>
          {activeSectionId && viewMode === 'edit' && (
            <motion.div 
              initial={{ x: 300 }}
              animate={{ x: 0 }}
              exit={{ x: 300 }}
              className="w-96 bg-white border-l border-black dark:border-white p-8 overflow-y-auto shrink-0 shadow-2xl dark:bg-slate-900"
            >
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-black dark:border-white">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Edit Selection</h3>
                <button 
                  onClick={() => setActiveSectionId(null)} 
                  className="px-4 py-2 bg-white text-gray-700 font-bold text-sm rounded-none hover:bg-gray-200 transition-colors"
                >
                  Done
                </button>
              </div>

              {localSections.find(s => s.id === activeSectionId) && (
                <div className="space-y-8">
                  {['hero', 'story', 'notice'].includes(localSections.find(s => s.id === activeSectionId)!.type) && (
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Headline Text</label>
                      <Input 
                        value={(localSections.find(s => s.id === activeSectionId) as any).title || ''} 
                        onChange={(e) => updateSection(activeSectionId, { title: e.target.value })}
                        className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                      />
                    </div>
                  )}

                  {localSections.find(s => s.id === activeSectionId)!.type === 'hero' && (
                    <>
                      <div className="space-y-3">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Subtitle</label>
                        <Input 
                          value={(localSections.find(s => s.id === activeSectionId) as HeroSection).subtitle || ''} 
                          onChange={(e) => updateSection(activeSectionId, { subtitle: e.target.value })}
                          className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                          placeholder="Subtitle or tagline..."
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Button Text</label>
                          <Input 
                            value={(localSections.find(s => s.id === activeSectionId) as HeroSection).ctaText || ''} 
                            onChange={(e) => updateSection(activeSectionId, { ctaText: e.target.value })}
                            className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                            placeholder="e.g. Learn More"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Button URL</label>
                          <Input 
                            value={(localSections.find(s => s.id === activeSectionId) as HeroSection).ctaUrl || ''} 
                            onChange={(e) => updateSection(activeSectionId, { ctaUrl: e.target.value })}
                            className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {localSections.find(s => s.id === activeSectionId)!.type === 'text' && (
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">HTML Content</label>
                      <textarea 
                        className="w-full h-64 px-4 py-3 text-base border-2 border-black dark:border-white rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono dark:bg-slate-950"
                        value={(localSections.find(s => s.id === activeSectionId) as TextSection).htmlContent}
                        onChange={(e) => updateSection(activeSectionId, { htmlContent: e.target.value })}
                        placeholder="<p>Enter HTML...</p>"
                      />
                    </div>
                  )}

                  {['story', 'notice'].includes(localSections.find(s => s.id === activeSectionId)!.type) && (
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Main Content</label>
                      <textarea 
                        className="w-full h-48 px-4 py-3 text-base border-2 border-black dark:border-white rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-950"
                        value={(localSections.find(s => s.id === activeSectionId) as any).content}
                        onChange={(e) => updateSection(activeSectionId, { content: e.target.value })}
                      />
                    </div>
                  )}

                  {localSections.find(s => s.id === activeSectionId)!.type === 'notice' && (
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Severity</label>
                      <div className="flex gap-3">
                        {['info', 'warning', 'error'].map((sev) => (
                          <button 
                            key={sev}
                            onClick={() => updateSection(activeSectionId, { severity: sev as any })}
                            className={cn(
                              "flex-1 py-3 text-sm font-bold capitalize rounded-none border-2 transition-all",
                              (localSections.find(s => s.id === activeSectionId) as NoticeSection).severity === sev
                                ? "bg-navy-900 border-navy-900 text-white"
                                : "bg-white border-black dark:border-white text-gray-600 hover:border-black dark:border-white dark:bg-slate-900"
                            )}
                          >
                            {sev}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {['hero', 'image', 'story'].includes(localSections.find(s => s.id === activeSectionId)!.type) && (
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Media URL</label>
                      <Input 
                        value={(localSections.find(s => s.id === activeSectionId) as any).imageUrl || ''} 
                        onChange={(e) => updateSection(activeSectionId, { imageUrl: e.target.value })}
                        className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                        placeholder="https://..."
                      />
                    </div>
                  )}

                  {localSections.find(s => s.id === activeSectionId)!.type === 'image' && (
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Caption</label>
                      <Input 
                        value={(localSections.find(s => s.id === activeSectionId) as ImageSection).caption || ''} 
                        onChange={(e) => updateSection(activeSectionId, { caption: e.target.value })}
                        className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                        placeholder="Photo description or credit..."
                      />
                    </div>
                  )}

                  {localSections.find(s => s.id === activeSectionId)!.type === 'button' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Button Label</label>
                        <Input 
                          value={(localSections.find(s => s.id === activeSectionId) as any).text || ''} 
                          onChange={(e) => updateSection(activeSectionId, { text: e.target.value })}
                          className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                          placeholder="e.g. Join Meeting"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Destination URL</label>
                        <Input 
                          value={(localSections.find(s => s.id === activeSectionId) as any).url || ''} 
                          onChange={(e) => updateSection(activeSectionId, { url: e.target.value })}
                          className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  )}

                  {localSections.find(s => s.id === activeSectionId)!.type === 'story' && (
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Author Name</label>
                      <Input 
                        value={(localSections.find(s => s.id === activeSectionId) as StorySection).author || ''} 
                        onChange={(e) => updateSection(activeSectionId, { author: e.target.value })}
                        className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                      />
                    </div>
                  )}

                  {localSections.find(s => s.id === activeSectionId)!.type === 'event_embed' && (
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Event ID</label>
                      <Input 
                        value={(localSections.find(s => s.id === activeSectionId) as EventEmbedSection).eventId || ''} 
                        onChange={(e) => updateSection(activeSectionId, { eventId: e.target.value })}
                        className="bg-white border-2 border-black dark:border-white px-4 py-3 text-base dark:bg-slate-950"
                        placeholder="evt_..."
                      />
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
